import { IL10N } from '../../Common/l10n-keys';
import {
    ISalesDesignationView,
    ICurrencyInformation,
    ITelemetryData,
} from '../articleTileGrid/article-tile/article-tile.d';
import { ICategory, IKioskInfo } from '../categorypage/categorypage.d';
import { IFasBarData } from '../fas-bar/fas-bar.d';
import { GlobalState } from '../globalState/globalState';
import { ArticleTilesAjaxCaller } from '../articleTileGrid/ArticleTilesAjaxCaller';
import { ICollectionLineConfig } from '../../Common/contexts/collectionLineContext';
import { IPrice } from '../flexSets/flexSetTile/flex-set-tile.d';

export interface ISearchResultPageProps {
    l10n: IL10N;
    searchResultPageProps: ISearchResultPage;
    category: ICategory;
}

export interface ISearchResultPageState {
    salesDesignationViews: ISalesDesignationView[];
    pageNo: number;
    totalResultCount: number;
    pageEndOffset: number;
    totalPageCount: number;
    waitingForResponse: boolean;
    isBreadcrumbOnly: boolean;
    componentMounted: boolean;
    scrollPosition: number;
    activeDropDownFilterName: string;
    stickyHeaderDropDownFilterName: string;
    isStickyHeaderVisible: boolean;
    toggleToUpdateFasBar: boolean;
    infiniteScrollUpMode: boolean;
    infiniteScrollUpMinPageNo: number;
    isSearchBottomReached: boolean;
    hasFlexibleSets: boolean;
    flexibleSetContext: IFlexibleSetContext;
}

export interface ISearchResultPage {
    articleLists: IArticleList[];
    bottomPageSearchBoxFragment: IFragmentPlaceholder;
    bottomSuggestionLinks: ISuggestionLinks;
    currencyInformation?: ICurrencyInformation;
    filterContentViewModel: IFasBarData;
    languageSwitcherFragment: ILanguageSwitcherFragmentProps;
    originalRootPath: string;
    originalSearchTerm: string;
    pagingInformation: IPagingInformation;
    style: string;
    searchInsteadInfo: ISearchInsteadProps;
    topSuggestionLinks: ISuggestionLinks;
    type: SearchResultPageType;
    url: string;
    metaTagPageType: string;
    magicLink: string;
    cdnUrl: string;
    collectionLinesConfig: ICollectionLineConfig;
    flexibleSetContext: IFlexibleSetContext;
    enableKraken: boolean;
    enableSearchResultEventForKraken: boolean;
}

export interface ISearchFilteringResult {
    articles: ISalesDesignationView[];
    totalResultCount: number;
    totalPageCount: number;
    pageEndOffset: number;
    flexibleSetContext: IFlexibleSetContext;
}

export interface IFlexibleSetContext {
    flexibleSets: ISearchFlexibleSet[];
    setCurrencyInformation: ICurrencyInformation;
    flexSetButton: IFlexSetButton;
}

export interface IFlexSetButton {
    telemetryData: ITelemetryData;
    flexSetBaseCategoryLink: string;
}

export interface IArticleList {
    alternativeTarget: string;
    articles: ISalesDesignationView[];
    filterAndSortBar: IFilterAndSortForSearch;
    isAlternative: boolean;
    paging: IPagingInformation;
    searchString: string;
    style: string;
    telemetryDataViewModel: ITelemetryData;
    totalResultCount: number;
    types: string[];
}

export interface ITeaserViewProps {
    articleLists: IArticleList[];
    currencyInformation?: ICurrencyInformation;
    kiosk: IKioskInfo;
    l10n: IL10N;
    isMobile: boolean;
    portal: string;
    culture: string;
    isGlobal: boolean;
    globalState: GlobalState;
    articleTilesAjaxCaller: ArticleTilesAjaxCaller;
    magicLink: string;
}

export interface ISearchInsteadProps {
    htmlEncodedAppropriateResultNotFoundText: string;
    htmlEncodedSearchInsteadText: string;
    originalSearchTerm: string;
    soldOutArticles: ISoldOutArticle[];
    style: string;
    topPageSearchBoxFragment: IFragmentPlaceholder;
    l10n: IL10N;
}

export interface IBottomSearchboxProps {
    style: string;
    l10n: IL10N;
}

export interface ISuggestionLinks {
    labelLocalizationKey: string;
    style: string;
    suggestionLinks: ISuggestionLink[];
}

export interface ISuggestionLinksState {
    isScrollable: boolean;
    showLeftChevron: boolean;
    showRightChevron: boolean;
    isDesktop: boolean;
}

export interface ISuggestionLink {
    searchTerm: string;
    portal: string;
    culture: string;
    target: string;
    telemetryDataViewModel: ITelemetryData;
}

export interface ILanguageSwitcherFragmentProps {
    culture: string;
    portal: string;
    fragment: IFragmentPlaceholder;
    telemetryDataViewModel: ITelemetryData;
    style: string;
    magicLink: string;
}

export interface ISoldOutArticle {
    articleName: string;
    salesArticleNo: string;
}

export interface IFilterAndSortForSearch {
    breadcrumbViewModel: IFragmentPlaceholder;
    filterAndSortViewModel: IFragmentPlaceholder;
    mainSidePanelViewModel: IFragmentPlaceholder;
}

export interface IFragmentPlaceholder {
    dClass: string;
    fallbackSource: string;
    features: string;
    ignoreErrors?: boolean;
    source: string;
    timeout?: number;
}

export interface IPagingInformation {
    pageNumber: number;
    totalPageCount: number;
    totalResultCount: number;
    pageEndOffset: number;
}

export enum SearchResultPageType {
    Empty,
    Regular,
    FullAlternativeView,
    TeaserAlternativeView,
}

export enum OriginType {
    LanguageSwitcher,
    RelatedTerm,
    TypoCorrection,
    UmbrellaTerm,
}

export enum ContentTypes {
    ArticleTile,
    LanguageSwitcher,
    ShowMoreArticles,
    SuggestionPillBottom,
    SuggestionPillTop,
}

export function SearchResultPagePropsToSdvMapper(
    props: ISearchResultPageProps
): ISalesDesignationView[] {
    const salesDesignationViews: ISalesDesignationView[] = [];

    props.searchResultPageProps.articleLists.map((list) => {
        list.articles.map((article) => salesDesignationViews.push(article));
    });

    return salesDesignationViews;
}

export interface IMetaEventForSearchResult {
    search_string: string;
    content_ids: string[];
}

export interface IFlexibleSetsWrapperProps {
    flexibleSetContext: IFlexibleSetContext;
    portal: string;
    culture: string;
    searchTerm: string;
    magicLink: string;
    l10n: IL10N;
    kiosk: IKioskInfo;
    isMobile: boolean;
    isGlobal: boolean;
    searchPageStyle: string;
}

export interface ISearchFlexibleSet {
    price: ISearchFlexibleSetPrice;
    fsCode: string;
    flexibleSetImage?: string;
    salesDesignation: string;
    isAvailable: boolean;
    navigationKey: string[];
    shortLink: string;
    url: string;
    sortedBricks: ISearchBrickItem[];
}

export interface ISearchFlexibleSetPrice {
    originalPrice: IPrice;
    price: IPrice;
    discountType: ISearchFlexibleSetDiscountType;
    discountPercentage: number;
}

export enum ISearchFlexibleSetDiscountType {
    DynamicPrice = 'DynamicPrice',
    FixedPrice = 'FixedPrice',
    FreeItem = 'FreeItem',
}

export interface ISearchBrickItem {
    articleName: string;
    articleImage: string;
    brickNo: number;
    brickItemNo: number;
    quantity: number;
    isFree: boolean;
}
